import React, { useState, useEffect, useRef, useContext } from "react";

import { useNavigate, useParams } from "react-router-dom";
import { parseISO, format, isSameDay, isSameYear } from "date-fns";
import {
	AlarmClock,
	AlertCircle,
	Loader2,
	MailOpen,
	Pin,
	PinOff,
	Star,
	User,
	Users,
} from "lucide-react";

import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import { canalDoTicket } from "../../helpers/canalDoTicket";
import TermoDestacado from "../TermoDestacado";
import {
  estadoDaPausa,
  esperaDoTicket,
  faltaResponder,
  nivelDeEscalada,
  slaVencendo,
} from "../../helpers/tempoDeEspera";
import { rotuloPresenca } from "../../hooks/usePresence";
import useLayoutTickets from "../../hooks/useLayoutTickets";
import usePrefTickets from "../../hooks/usePrefTickets";
import { IDS_COLUNAS_PAINEL, classeDaColuna } from "./colunasPainel";
import { IDS_ITENS_DO_CARTAO } from "./itensDoCartao";
import { IDS_BOTOES_DA_LINHA } from "./botoesDaLinha";
import TicketPeekDialog from "../TicketPeekDialog";
import usePermissions from "../../hooks/usePermissions";
import TransferTicketModal from "../TransferTicketModal";
import ResolverTicketModal from "../ResolverTicketModal";
import SeloDeEncerramento from "../SeloDeEncerramento";
import { Checkbox } from "../ui/checkbox";
import ReabrirTicketModal, {
  carregarMotivosDeReabertura,
} from "../ReabrirTicketModal";
import { cn } from "../../lib/utils";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import { avisarContadores } from "../../services/contadoresDaLista";
import MarkdownWrapper from "../MarkdownWrapper";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "react-toastify";
import { corDeTextoSobre } from "../../helpers/corDeTexto";
import toastError from "../../errors/toastError";

// Selo sólido e curto, como no fservice: fundo colorido, texto branco em
// caixa alta e 9px. Reconhece-se pela cor antes de ser lido.
const Selo = ({ children, style, className, title }) => (
  <span
    title={title}
    style={style}
    className={cn(
      "max-w-36 truncate rounded-[3px] px-1 py-[2px] text-[9px] font-bold uppercase leading-none text-white",
      className,
    )}
  >
    {children}
  </span>
);

// Ação da linha: texto pequeno em azul, sem moldura — o mesmo tratamento da
// referência, onde as ações ficam discretas até a pessoa precisar delas.
const Acao = ({ children, onClick, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    className="rounded px-1.5 py-0.5 text-[11px] font-medium text-[#0872B9] transition-colors hover:bg-[#0872B9]/10 disabled:opacity-40 dark:text-sky-300 dark:hover:bg-sky-300/10"
  >
    {children}
  </button>
);

const cortar = (texto, tamanho) => {
  const t = String(texto || "");

  return t.length > tamanho ? `${t.slice(0, tamanho)}...` : t;
};

// A última mensagem chega crua: localização vem como imagem em base64 e
// contato como VCARD. Mostrar isso na lista não diz nada a quem lê.
const previaDaMensagem = (lastMessage) => {
  const texto = String(lastMessage || "");

  if (texto.includes("data:image/png;base64")) return "Localização";
  if (texto.includes("BEGIN:VCARD")) return "Contato";

  return cortar(texto, 40);
};

// A preferência chega por prop, não por hook: são muitas linhas na tela, e
// cada uma com o seu próprio estado não saberia que a outra mudou.
const TicketListItem = ({
  ticket,
  presenca,
  // Seleção múltipla. `selecionado` é o estado desta linha; `aoSelecionar`
  // avisa a lista. `selecionando` liga a caixa de todas as linhas: sem isso a
  // primeira marcação exigiria acertar um alvo que só aparece no hover.
  selecionado = false,
  selecionando = false,
  aoSelecionar,
  colunasVisiveis = IDS_COLUNAS_PAINEL,
  itensVisiveis = IDS_ITENS_DO_CARTAO,
  // "compacta": uma linha por atendimento no Clássico e no Foco.
  densidade = "confortavel",
  botoesVisiveis = IDS_BOTOES_DA_LINHA,
  // Preferência de quem usa, por prop e não por hook: ver `pausaAlerta`.
  piscarPausa = true,
  // A partir de qual volta o selo de recorrência aparece.
  recorrenteMinimo = 2,
  fixado = false,
  // Na busca a lista mistura aberto, aguardando e resolvido: sem o selo, a
  // pessoa lê uma conversa encerrada achando que está em andamento.
  mostrarStatus = false,
  // Texto da busca da lista. Com ele a linha diz por que apareceu e abre a
  // conversa na mensagem achada.
  termoBusca = "",
}) => {
  // A data do cartão é a da última MENSAGEM — a mesma por que a lista ordena.
  //
  // Antes mostrava `updatedAt`, que muda a cada toque no atendimento: a linha
  // dizia "08/09" logo acima de outra dizendo "02/09" e parecia lista
  // desordenada, quando o que estava errado era o número exibido. Conversa sem
  // mensagem nenhuma cai no `createdAt`: é quando ela passou a existir.
  const dataDoCartao =
    ticket.lastMessageAt || ticket.createdAt || ticket.updatedAt;

  const rotuloDeStatus =
    ticket.status === "closed"
      ? "Resolvido"
      : ticket.status === "pending"
        ? "Aguardando"
        : "Aberto";
  const seloDeStatus = mostrarStatus ? (
    <span
      className={cn(
        "shrink-0 rounded-sm px-1.5 py-0.5 text-[10px] font-medium",
        ticket.status === "closed"
          ? "bg-muted text-muted-foreground"
          : ticket.status === "pending"
            ? "bg-amber-500/15 text-amber-700 dark:text-amber-400"
            : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
      )}
    >
      {rotuloDeStatus}
    </span>
  ) : null;

  // Na Tabela o selo escrito disputava a célula do nome ("S3 Lab…"): lá a
  // situação vira bolinha, com o nome por extenso para leitor de tela e no
  // balão.
  const pontoDeStatus = mostrarStatus ? (
    <span
      title={rotuloDeStatus}
      className={cn(
        "h-2 w-2 shrink-0 rounded-full",
        ticket.status === "closed"
          ? "bg-muted-foreground/50"
          : ticket.status === "pending"
            ? "bg-amber-500"
            : "bg-emerald-500",
      )}
    >
      <span className="sr-only">{rotuloDeStatus}</span>
    </span>
  ) : null;

  // Achou pelo conteúdo: abre a conversa NA mensagem, com a busca da
  // conversa preenchida — não no fim, onde a pessoa teria de procurar de novo.
  const casouPor = termoBusca ? ticket.buscaCasouPor : null;
  const destino =
    casouPor === "mensagem" && ticket.buscaMensagemId
      ? `/tickets/${ticket.id}?mensagem=${encodeURIComponent(
          ticket.buscaMensagemId,
        )}&busca=${encodeURIComponent(termoBusca)}`
      : `/tickets/${ticket.id}`;
  const trechoDaBusca =
    casouPor === "mensagem" && ticket.buscaTrecho ? (
      // Sem os marcadores do WhatsApp (*negrito*, _itálico_, ~riscado~):
      // no trecho eles apareciam crus. Só na exibição.
      <TermoDestacado
        texto={ticket.buscaTrecho.replace(/[*_~`]/g, "")}
        termo={termoBusca}
      />
    ) : null;
  // Achado na mensagem não leva selo: o trecho com o termo marcado já diz por
  // que a conversa apareceu, e o selo só roubava largura do nome.
  const seloDaBusca = casouPor && casouPor !== "mensagem" ? (
    <span
      title="Onde a busca encontrou o termo"
      className="shrink-0 rounded-sm bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary"
    >
      {casouPor === "nome"
        ? "Nome"
        : casouPor === "telefone"
          ? "Telefone"
          : "Mensagem"}
    </span>
  ) : null;

  const mostrar = (id) => itensVisiveis.includes(id);
  const temBotao = (id) => botoesVisiveis.includes(id);
  const navigate = useNavigate();
  // Grade de uma linha em vez de cartão. Quem escolhe é a preferência da
  // pessoa (Configurações › Aparência), não uma prop atravessando a lista.
  const compacto = useLayoutTickets() === "painel";
  // A densidade compacta reaproveita a linha do Painel (mesmas ações no
  // hover), mas sem as colunas: o que aparece segue os itens do cartão.
  const umaLinha = compacto || densidade === "compacta";
  const colunas = compacto ? colunasVisiveis : [];
  const [loading, setLoading] = useState(false);
  const [transferindo, setTransferindo] = useState(false);
  // Encerrar deixou de ser um clique só: o modal pede o motivo e pergunta se
  // o cliente confirma antes.
  const [resolvendo, setResolvendo] = useState(false);
  // Reabrir só pergunta o motivo se a empresa cadastrou algum.
  const [reabrindo, setReabrindo] = useState(false);
  const { ticketId } = useParams();
  const isMounted = useRef(true);
  const { user } = useContext(AuthContext);

  // Ler a conversa antes de aceitar é permissão de cargo. Aqui ela só explica
  // o que a tela mostra — quem barra de verdade é o backend, que devolve 403
  // e prévia vazia para quem não pode. O administrador passa por cima do
  // cargo, como no servidor: sem isso o cartão dizia "disponível após
  // aceitar" para quem enxerga tudo, porque a chave nova não está no cargo
  // antigo gravado no banco.
  const { temPermissao } = usePermissions();
  // Antes de aceitar, transferir e ignorar são permissão à parte: o cargo
  // "Usuário" assume o que pegou em vez de escolher cliente e devolver o
  // resto. Quem administra segue redistribuindo a fila sem assumir nada.
  const podeMexerNaEspera = (chave) =>
    ticket.status !== "pending" || temPermissao(chave);
  const aguardandoSemEspiada =
    ticket.status === "pending" &&
    user?.profile !== "admin" &&
    !temPermissao("tickets:peekPending");

  // Favorito é marcação pessoal: o estado local responde ao clique na hora e
  // só volta atrás se o servidor recusar.
  const [favorito, setFavorito] = useState(Boolean(ticket.favoritado));

  useEffect(() => {
    // O ticket que chega pelo socket é o mesmo para todo mundo e não carrega
    // "favoritado"; sem esta guarda, uma mensagem nova apagaria a estrela.
    if (ticket.favoritado !== undefined) {
      setFavorito(Boolean(ticket.favoritado));
    }
  }, [ticket.favoritado]);

  // Marcar como não lida: só acende de volta a bolinha que abrir a conversa
  // apagou. É o "deixa comigo mais tarde" de quem abriu sem tempo de
  // responder — sem isto, o atendimento some do radar por ter sido lido.
  const [marcandoNaoLida, setMarcandoNaoLida] = useState(false);

  const marcarNaoLida = async (e) => {
    e.stopPropagation();

    if (marcandoNaoLida) return;
    setMarcandoNaoLida(true);

    try {
      await api.put(`/tickets/${ticket.id}/unread`);
      toast.success("Marcada como não lida.");
    } catch (err) {
      toastError(err);
    }

    setMarcandoNaoLida(false);
  };

  // Prender no topo da lista. Preferência da PESSOA, guardada em
  // `uiPreferences.ticketsFixados` — dois atendentes fixam coisas diferentes
  // da mesma fila, e por isso não é campo do atendimento.
  const [fixados, setFixados] = usePrefTickets("ticketsFixados", []);

  const alternarFixado = (e) => {
    e.stopPropagation();

    setFixados(
      fixados.includes(ticket.id)
        ? fixados.filter((id) => id !== ticket.id)
        : [ticket.id, ...fixados].slice(0, 20),
    );
  };

  const alternarFavorito = async (e) => {
    e.stopPropagation();

    const novo = !favorito;
    setFavorito(novo);

    try {
      if (novo) await api.post(`/ticket-favorites/${ticket.id}`);
      else await api.delete(`/ticket-favorites/${ticket.id}`);

      avisarContadores("favoritos");
    } catch (err) {
      setFavorito(!novo);
      toastError(err);
    }
  };

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const pedirReabertura = async () => {
    const motivos = await carregarMotivosDeReabertura();

    if (!motivos.length) {
      mudarStatus("open", user?.id, true);
      return;
    }

    setReabrindo(true);
  };

  const mudarStatus = async (status, userId, irParaOTicket) => {
    setLoading(true);
    try {
      await api.put(`/tickets/${ticket.id}`, { status, userId });
      if (irParaOTicket) navigate(`/tickets/${ticket.id}`);
    } catch (err) {
      toastError(err);
    }
    if (isMounted.current) setLoading(false);
  };

  // Mensagem que não saiu. Sem isto, quem não abrisse a conversa não
  // descobria — nem a que o robô tentou mandar, nem a do colega.
  const temFalha = Boolean(ticket.temFalha);

  const isSelected = ticketId && +ticketId === ticket.id;
  const naoLidas = Number(ticket.unreadMessages) || 0;

  const canal = canalDoTicket(ticket);

  // Atendimento resolvido não pertence mais a fila nenhuma: mostrar o selo
  // colorido dava a entender que ele ainda está na mão da Comercial. A coluna
  // `queueId` CONTINUA gravada — é dela que o relatório por fila tira "quantos
  // a Comercial resolveu", e apagá-la zeraria o histórico inteiro. O que sai é
  // só a promessa visual de atribuição ativa.
  const filaAtiva = ticket.status !== "closed" ? ticket.queue : null;
  const CanalIcone = canal.icone;
  const espera = esperaDoTicket(ticket);

  // O cliente falou por último e ninguém respondeu dentro do prazo da fila.
  // Não é o mesmo que espera: um atendimento já em andamento também pode
  // estar devendo resposta — e é esse que some do radar.
  const devendoResposta = faltaResponder(ticket);

  // Escalada de SLA já aplicada pelo worker. Não passa por preferência de
  // tela: é aviso, não enfeite — quem esconde perde exatamente o que precisa
  // ver.
  const escalada = nivelDeEscalada(ticket);

  // A janela final antes de estourar a meta da fila. Some sozinho quando
  // vira "Falta responder" — os dois nunca aparecem juntos.
  const vencendo = mostrar("slaVencendo") ? slaVencendo(ticket) : null;

  // Pausa de quem atende. A linha continua na lista de "Pausa"; nas outras, o
  // servidor esconde enquanto o prazo não vence. O vencido volta às listas
  // normais — e é onde o relógio cobra o retorno.
  const pausa = estadoDaPausa(ticket);

  // Prazo estourado pisca até alguém agir. É preferência de quem usa (som e
  // aviso do navegador moram no mesmo lugar), e desce por prop: a linha se
  // desenha às centenas, e um hook por linha seria um assinante por linha.
  const pausaAlerta = pausa?.vencido && piscarPausa;

  const tituloDaPausa = pausa
    ? pausa.vencido
      ? `Pausa vencida em ${pausa.rotulo} — retorne ao cliente`
      : pausa.comMensagem
        ? `Em pausa até ${pausa.rotulo} — o cliente mandou mensagem`
        : `Em pausa até ${pausa.rotulo}`
    : "";

  // Contato que chegou só com o identificador novo do WhatsApp não tem nome
  // nem telefone; sem isto a linha do atendimento aparece em branco e não dá
  // para saber de quem é a conversa.
  const nomeExibido =
    ticket.contact?.name || ticket.contact?.number || "Contato sem número";

  // A caixa fica antes de tudo na linha, nas duas variantes. Clicar nela não
  // pode abrir a conversa — é a única parte da linha que não navega.
  const caixaDeSelecao = aoSelecionar ? (
    <span
      className={cn(
        // Vaga fixa e centrada: sem largura própria a caixa encostava na borda
        // da linha — e na faixa de cor da fila, que mora nos 4px da esquerda —
        // aparecendo cortada no celular. A vaga também evita o texto pular
        // quando as caixas surgem no modo seleção.
        "flex h-6 w-6 shrink-0 items-center justify-center",
        // Fora do modo seleção ela só aparece no hover (ou no foco pelo
        // teclado): uma coluna de caixas vazias em toda linha rouba a atenção
        // de quem só quer ler a lista.
        //
        // Onde não existe hover (celular), esconder é esconder para sempre: a
        // caixa ficava invisível e só quem soubesse do lugar exato acertava o
        // toque. Ali ela fica à vista, esmaecida.
        !selecionando &&
          !selecionado &&
          "opacity-0 group-hover:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-40",
      )}
      // A vaga inteira é o alvo: com o clique só no Checkbox de 16px, os 4px
      // de cada lado eram zona morta — não selecionavam e nem abriam a
      // conversa. O Checkbox fica sem eventos para não disparar duas vezes.
      onClick={(e) => {
        e.stopPropagation();
        aoSelecionar(ticket);
      }}
    >
      <Checkbox
        checked={selecionado}
        aria-label={`Selecionar conversa com ${nomeExibido}`}
        tabIndex={-1}
        className="pointer-events-none"
      />
    </span>
  ) : null;

  const initials = (nomeExibido || "?")
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase();

  return (
    <>
      {/* Montado só quando abre: são dezenas de linhas na tela, e cada
          instância carrega estado, efeitos e uma consulta de departamentos. */}
      {transferindo && (
        <TransferTicketModal
          modalOpen={transferindo}
          onClose={() => setTransferindo(false)}
          ticketid={ticket.id}
          ticketWhatsappId={ticket.whatsappId}
        />
      )}

      <ReabrirTicketModal
        open={reabrindo}
        onClose={() => setReabrindo(false)}
        ticket={ticket}
        aoReabrir={() => navigate(`/tickets/${ticket.id}`)}
      />

      <ResolverTicketModal
        open={resolvendo}
        onClose={() => setResolvendo(false)}
        ticket={ticket}
        // Encerrar pela linha some com o atendimento da lista, mas a conversa
        // continuava aberta no painel ao lado — encerrada e ainda na tela.
        // Só sai quem estava lendo justamente este: resolver a linha de baixo
        // não pode fechar a conversa que a pessoa tem aberta.
        onResolved={() => {
          if (Number(ticketId) === Number(ticket.id)) navigate("/tickets");
        }}
      />

      {umaLinha && (
        <div
          role="button"
          tabIndex={0}
          // O atalho de teclado (j/k) anda pela lista pelo DOM: é o único
          // lugar onde a ordem visível de verdade existe, com filtro, busca e
          // aba já aplicados.
          data-ticket-id={ticket.id}
          data-ticket-status={ticket.status}
          className={cn(
            "group relative flex cursor-pointer items-center gap-3 border-b px-3 py-1.5 text-xs transition-colors hover:bg-accent/60",
            "focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring",
            isSelected && "bg-accent",
          )}
          onClick={() => navigate(destino)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              navigate(destino);
            }
          }}
        >
          {caixaDeSelecao}

          {!compacto && mostrar("faixaDaFila") && (
            <span
              className="absolute inset-y-0 left-0 w-1"
              style={{ backgroundColor: filaAtiva?.color || "transparent" }}
            />
          )}

          {!compacto && mostrar("avatar") && (
            <Avatar className="h-7 w-7 shrink-0">
              <AvatarImage
                src={ticket?.contact?.profilePicUrl}
                alt={nomeExibido}
              />
              <AvatarFallback className="text-[10px]">{initials}</AvatarFallback>
            </Avatar>
          )}

          {/* Espera é a única coluna que muda de cor: é por ela que o olho
              desce quando a lista tem vinte linhas. */}
          {compacto && (
          <span
            className={cn(
              "w-[68px] shrink-0 whitespace-nowrap text-right text-[11px] tabular-nums",
              devendoResposta
                ? "font-semibold text-rose-600 dark:text-rose-400"
                : espera
                  ? espera.classe
                  : "text-muted-foreground",
            )}
            title={
              devendoResposta
                ? `Falta responder há ${devendoResposta.rotulo}`
                : ticket.status === "pending"
                  ? "Esperando há"
                  : "Sem resposta há"
            }
          >
            {espera ? espera.rotulo : "—"}
          </span>
          )}

          {pausa && (
            <AlarmClock
              className={cn(
                "h-3.5 w-3.5 shrink-0",
                pausa.classe,
                // Vencido cresce e pisca: é a diferença entre "está pausado" e
                // "você prometeu voltar e a hora passou".
                pausaAlerta && "h-4 w-4 animate-pulse",
              )}
              title={tituloDaPausa}
              aria-label={tituloDaPausa}
            />
          )}

          {(compacto || mostrar("seloCanal")) && (
          <span
            title={canal.rotulo}
            aria-label={`Canal: ${canal.rotulo}`}
            className={cn(
              "flex h-4 w-4 shrink-0 items-center justify-center rounded-full",
              canal.classe,
            )}
          >
            <CanalIcone className="h-2.5 w-2.5" />
          </span>
          )}

          {temFalha && (
            <AlertCircle
              className="h-3.5 w-3.5 shrink-0 text-destructive"
              title="Tem mensagem que não foi enviada"
              aria-label="Tem mensagem que não foi enviada"
            />
          )}

          {/* Selos dentro da célula do contato: soltos na linha, caíam sob
              a coluna seguinte da Tabela e desalinhavam do cabeçalho. */}
          <span className="flex min-w-[104px] flex-[1.2] items-center gap-1.5">
            <span
              className={cn(
                "min-w-0 truncate",
                naoLidas > 0 ? "font-bold" : "font-medium",
              )}
            >
              {ticket.isGroup ? "👥 " : ""}
              {casouPor === "nome" ? (
                <TermoDestacado texto={nomeExibido} termo={termoBusca} />
              ) : (
                nomeExibido
              )}
            </span>
            {pontoDeStatus}
            {seloDaBusca}
          </span>

          {!compacto && (mostrar("previa") || trechoDaBusca) && (
            <span className="min-w-0 flex-[2] truncate text-muted-foreground">
              {trechoDaBusca ||
                rotuloPresenca(presenca) ||
                previaDaMensagem(ticket.lastMessage)}
            </span>
          )}

          {colunas.includes("ultimaMensagem") && (
            <span
              className={cn(
                classeDaColuna("ultimaMensagem"),
                "text-muted-foreground",
              )}
            >
              {trechoDaBusca ||
                rotuloPresenca(presenca) ||
                previaDaMensagem(ticket.lastMessage)}
            </span>
          )}

          {colunas.includes("fila") && (
            <span
              className={cn(classeDaColuna("fila"), "text-muted-foreground")}
            >
              {/* A coluna do layout Painel tem cabeçalho fixo: some o texto,
                  não a célula, senão as colunas seguintes andam para a
                  esquerda e desalinham da linha de cima. */}
              {ticket.status === "closed" ? "—" : filaAtiva?.name || "Sem departamento"}
            </span>
          )}

          {colunas.includes("atendente") && (
            <span
              className={cn(
                classeDaColuna("atendente"),
                "text-muted-foreground",
              )}
            >
              {ticket.user?.name || "—"}
            </span>
          )}

          {colunas.includes("motivoEncerramento") && (
            <span className={classeDaColuna("motivoEncerramento")}>
              <SeloDeEncerramento ticket={ticket} />
            </span>
          )}

          {/* As ações não moram na grade: aparecem quando o ponteiro (ou o
              teclado) chega na linha, e no toque ficam sempre visíveis, que
              lá não existe passar o mouse. */}
          {/* `hidden` de verdade, não opacidade: a coluna precisa devolver a
              largura para a prévia da mensagem quando ninguém está com o
              ponteiro na linha. No toque, onde não existe hover, elas ficam
              sempre visíveis — menos na densidade compacta: lá não cabem
              ao lado de espera e data, e ficam para quando abre. */}
          <span
            className={cn(
              "hidden shrink-0 items-center gap-0.5 lg:group-hover:flex lg:group-focus-within:flex",
              compacto && "max-lg:flex",
            )}
          >
            {loading && (
              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
            )}

            {temBotao("aceitar") && ticket.status === "pending" && (
              <Acao
                disabled={loading}
                onClick={() => mudarStatus("open", user?.id, true)}
              >
                {i18n.t("ticketsList.buttons.accept")}
              </Acao>
            )}

            {temBotao("transferir") &&
              ticket.status !== "closed" &&
              podeMexerNaEspera("tickets:transferPending") && (
              <Acao disabled={loading} onClick={() => setTransferindo(true)}>
                Transferir
              </Acao>
            )}

            {temBotao("resolver") && ticket.status === "open" && (
              <Acao disabled={loading} onClick={() => setResolvendo(true)}>
                Resolver
              </Acao>
            )}

            {temBotao("reabrir") && ticket.status === "closed" && (
              <Acao
                disabled={loading}
                onClick={pedirReabertura}
              >
                Reabrir
              </Acao>
            )}

            {temBotao("fixar") && (
              <button
                type="button"
                aria-label={fixado ? "Soltar do topo" : "Fixar no topo"}
                aria-pressed={fixado}
                title={fixado ? "Soltar do topo" : "Fixar no topo"}
                onClick={alternarFixado}
                className={cn(
                  "flex h-6 w-6 items-center justify-center rounded-full transition-colors",
                  fixado
                    ? "text-primary hover:bg-primary/10"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                {fixado ? (
                  <PinOff className="h-3.5 w-3.5" />
                ) : (
                  <Pin className="h-3.5 w-3.5" />
                )}
              </button>
            )}

            {temBotao("naoLida") && ticket.unreadMessages === 0 && (
              <button
                type="button"
                aria-label="Marcar como não lida"
                title="Marcar como não lida"
                onClick={marcarNaoLida}
                disabled={marcandoNaoLida}
                className="flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <MailOpen className="h-3.5 w-3.5" />
              </button>
            )}

            {temBotao("favoritar") && (
            <button
              type="button"
              aria-label={
                favorito ? "Remover dos favoritos" : "Marcar como favorita"
              }
              aria-pressed={favorito}
              onClick={alternarFavorito}
              className={cn(
                "flex h-6 w-6 items-center justify-center rounded-full transition-colors",
                favorito
                  ? "text-amber-500 opacity-100 hover:bg-amber-500/10"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              <Star
                className="h-3.5 w-3.5"
                fill={favorito ? "currentColor" : "none"}
              />
            </button>
            )}
          </span>

          {!compacto && mostrar("espera") && espera && (
            <span
              className={cn(
                "shrink-0 whitespace-nowrap text-[11px] font-semibold tabular-nums",
                espera.classe,
              )}
              title={
                ticket.status === "pending" ? "Esperando há" : "Sem resposta há"
              }
            >
              {espera.rotulo}
            </span>
          )}

          <span className="flex w-14 shrink-0 items-center justify-end gap-1.5">
            {naoLidas > 0 && (
              <span className="flex h-4 min-w-[16px] items-center justify-center rounded-full bg-emerald-500 px-1 text-[10px] font-bold tabular-nums text-white">
                {naoLidas}
              </span>
            )}
            <span className="text-[11px] tabular-nums text-muted-foreground">
              {isSameDay(parseISO(dataDoCartao), new Date())
                ? format(parseISO(dataDoCartao), "HH:mm")
                : format(parseISO(dataDoCartao), "dd/MM")}
            </span>
          </span>
        </div>
      )}

      {!umaLinha && (
      <div
        role="button"
        tabIndex={0}
        data-ticket-id={ticket.id}
        data-ticket-status={ticket.status}
        className={cn(
          // `group` para a caixa de seleção aparecer no hover da linha —
          // fora do modo seleção ela fica invisível.
          "group relative flex cursor-pointer gap-3 border-b px-3 py-2.5 transition-colors hover:bg-accent/60",
          "focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring",
          isSelected && "bg-accent",
        )}
        onClick={() => navigate(destino)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            navigate(destino);
          }
        }}
      >
        {caixaDeSelecao}

        {/* Faixa do departamento na borda, como na referência. */}
        {mostrar("faixaDaFila") && (
          <span
            className="absolute inset-y-0 left-0 w-1"
            style={{ backgroundColor: filaAtiva?.color || "transparent" }}
          />
        )}

        {mostrar("avatar") && (
          <Avatar className="h-[50px] w-[50px] shrink-0 self-start">
            <AvatarImage
              src={ticket?.contact?.profilePicUrl}
              alt={nomeExibido}
            />
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
        )}

        <div className="min-w-0 flex-1">
          {/* Nome, com o ícone do canal antes e o "espiar" depois. */}
          <div className="flex min-w-0 items-center gap-1.5 pr-14">
            {ticket.isGroup && (
              <Users className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            )}

            {mostrar("seloCanal") && (
              <span
                title={canal.rotulo}
                aria-label={`Canal: ${canal.rotulo}`}
                className={cn(
                  "flex h-4 w-4 shrink-0 items-center justify-center rounded-full",
                  canal.classe,
                )}
              >
                <CanalIcone className="h-2.5 w-2.5" />
              </span>
            )}

            <span
              className={cn(
                "min-w-0 truncate text-sm",
                naoLidas > 0 ? "font-bold" : "font-medium",
              )}
            >
              {casouPor === "nome" ? (
                <TermoDestacado
                  texto={cortar(nomeExibido, 60)}
                  termo={termoBusca}
                />
              ) : (
                cortar(nomeExibido, 60)
              )}
            </span>

            {temBotao("espiar") && !aguardandoSemEspiada && (
              <TicketPeekDialog ticket={ticket} />
            )}
          </div>

          {(mostrar("previa") || trechoDaBusca) && (
          <p
            className={cn(
              "mt-0.5 truncate pr-14 text-xs",
              naoLidas > 0
                ? "font-bold text-foreground"
                : "text-muted-foreground",
            )}
          >
            {/* Na busca, o trecho achado vence tudo: é a resposta para
                "por que esta conversa apareceu?". Digitando vence a última
                mensagem: é o que acontece agora. */}
            {trechoDaBusca ? (
              <span className="font-normal text-muted-foreground">
                {trechoDaBusca}
              </span>
            ) : rotuloPresenca(presenca) ? (
              <span className="font-medium text-emerald-600 dark:text-emerald-400">
                {rotuloPresenca(presenca)}
              </span>
            ) : ticket.lastMessage ? (
              <MarkdownWrapper>
                {previaDaMensagem(ticket.lastMessage)}
              </MarkdownWrapper>
            ) : aguardandoSemEspiada ? (
              // O cartão continua dizendo QUEM está esperando e há quanto
              // tempo; o que some é o assunto — era por ele que se escolhia
              // cliente antes de aceitar.
              <span className="italic">Conversa disponível após aceitar</span>
            ) : (
              <>&nbsp;</>
            )}
          </p>
          )}

          {/* Canal, conexão, fila e atendente. O canal por extenso porque o
              selo do avatar tem 4px: com WhatsApp, Instagram, Telegram e
              Messenger na mesma lista, é ele que diz onde a conversa está. */}
          <div className="mt-1 flex flex-wrap items-center gap-[3px]">
            {/* Na fileira que quebra linha, não na do nome: lá eles
                empurravam o nome para fora em coluna estreita. */}
            {seloDeStatus}
            {seloDaBusca}

            {temFalha && (
              <Selo
                title="Tem mensagem que não foi enviada"
                style={{ backgroundColor: "#dc2626" }}
              >
                Não enviada
              </Selo>
            )}

            {vencendo && (
              <Selo
                title={`Meta deste departamento: ${vencendo.meta}min para responder`}
                style={{ backgroundColor: "#d97706" }}
              >
                {vencendo.rotulo}
              </Selo>
            )}

            {devendoResposta && (
              <Selo
                title={`O cliente falou há ${devendoResposta.rotulo} e ninguém respondeu`}
                style={{ backgroundColor: "#e11d48" }}
              >
                Falta responder
              </Selo>
            )}

            {/* Dinheiro parado: a resposta de "quais das minhas 50 conversas
                têm venda esperando gente?" sem abrir nenhuma. O dado vem
                agregado na listagem — nunca uma chamada por linha. */}
            {mostrar("vendaAguardando") && ticket.vendaAguardando && (
              <Selo
                title={
                  ticket.vendaAguardando === "pagamento"
                    ? "Este contato tem pedido esperando pagamento"
                    : "Este contato tem orçamento esperando resposta"
                }
                style={{ backgroundColor: "#d97706" }}
              >
                {ticket.vendaAguardando === "pagamento"
                  ? "A pagar"
                  : "Orçamento aguardando"}
              </Selo>
            )}

            {/* Qual volta é esta. O atendimento encerrado e o que nasce
                quando o cliente volta são separados de propósito; sem este
                sinal a segunda conversa parecia a primeira, e quem atende
                começava do zero sem saber que já houve outras. O número vem
                agregado na listagem — nunca uma chamada por linha. */}
            {mostrar("atendimentoRecorrente") &&
              ticket.ordemDoContato >= recorrenteMinimo && (
              <Selo
                title={`Este é o ${ticket.ordemDoContato}º atendimento deste contato — o histórico anterior abre dentro da conversa`}
                style={{ backgroundColor: "#7c3aed" }}
              >
                {ticket.ordemDoContato}º atendimento
              </Selo>
            )}

            {pausa && (
              <Selo
                title={tituloDaPausa}
                className={cn(pausaAlerta && "animate-pulse")}
                style={{
                  backgroundColor: pausa.vencido
                    ? "#e11d48"
                    : pausa.comMensagem
                      ? "#d97706"
                      : "#0284c7",
                }}
              >
                {pausa.vencido ? "Pausa vencida" : `Em pausa até ${pausa.rotulo}`}
              </Selo>
            )}

            {mostrar("seloCanal") && (
              <Selo style={{ backgroundColor: canal.corSelo }}>
                {canal.rotulo}
              </Selo>
            )}

            {/* Neutro: a cor da marca agora identifica o canal, e repeti-la
                aqui faria os dois selos virarem um borrão só. */}
            {mostrar("seloConexao") && ticket.whatsapp?.name && (
              <Selo style={{ backgroundColor: "#525252" }}>
                {ticket.whatsapp.name}
              </Selo>
            )}

            {escalada && (
              <Selo
                title={`Escalada de SLA: ${
                  escalada.rotulo || "nível " + ticket.slaEscalationLevel
                } — passou de ${escalada.minutos}min sem resposta`}
                style={{
                  backgroundColor: escalada.cor,
                  color: corDeTextoSobre(escalada.cor),
                }}
              >
                {escalada.rotulo || `Nível ${ticket.slaEscalationLevel}`}
              </Selo>
            )}

            {mostrar("seloFila") && ticket.status !== "closed" && (
              <Selo
                style={{
                  backgroundColor: filaAtiva?.color || "#7c7c7c",
                  color: corDeTextoSobre(filaAtiva?.color || "#7c7c7c"),
                }}
              >
                {filaAtiva?.name || "Sem departamento"}
              </Selo>
            )}

            {/* De quem é a conversa hoje. Na aba "Do departamento" aparece conversa
                de colega e qualquer um pode puxá-la — o rótulo explícito é o
                que evita assumir sem perceber que já tem atendente. */}
            {mostrar("seloAtendente") && ticket.user?.name && (
              <Selo
                title={`Atribuído a ${ticket.user.name}`}
                style={{
                  backgroundColor:
                    ticket.userId === user?.id ? "#000000" : "#b45309",
                }}
              >
                {ticket.userId === user?.id
                  ? ticket.user.name
                  : `Atribuído a ${ticket.user.name}`}
              </Selo>
            )}

            {/* Só na aba Resolvidos: o selo some sozinho nas outras porque um
                atendimento aberto não tem motivo de encerramento. */}
            {mostrar("motivoEncerramento") && (
              <SeloDeEncerramento ticket={ticket} />
            )}
          </div>

          {/* Etiqueta do ATENDIMENTO: vazada, só o contorno na cor dela.
              A regra vale no sistema inteiro — cheia é da pessoa, vazada é da
              conversa. Uma diferença de PESO, que se lê de longe, em vez de
              duas coisas iguais com significados diferentes. */}
          {mostrar("etiquetas") && ticket.tags?.length > 0 && (
            <div className="mt-1 flex flex-wrap items-center gap-[3px]">
              {ticket.tags.map((tag) => (
                <Selo
                  key={tag.id}
                  title={`${tag.name} — etiqueta desta conversa`}
                  className="border bg-transparent"
                  style={{ borderColor: tag.color, color: tag.color }}
                >
                  {tag.name}
                </Selo>
              ))}
            </div>
          )}

          {/* Etiqueta da PESSOA: cheia, com o ícone de pessoinha.
              Cheia porque é a que vale sempre — a que descreve quem está do
              outro lado pesa mais do que o estado de uma conversa. O ícone
              fica por cima disso para quem enxerga mal, que não distingue
              cheia de vazada. */}
          {mostrar("etiquetasDoContato") &&
            ticket.etiquetasDoContato?.length > 0 && (
              <div className="mt-1 flex flex-wrap items-center gap-[3px]">
                {ticket.etiquetasDoContato.map((tag) => (
                  <Selo
                    key={`c${tag.id}`}
                    title={`${tag.name} — etiqueta da pessoa, vale para todas as conversas dela`}
                    className="inline-flex items-center gap-[3px]"
                    style={{
                      backgroundColor: tag.color,
                      color: corDeTextoSobre(tag.color),
                    }}
                  >
                    <User className="h-2.5 w-2.5 shrink-0" />
                    {tag.name}
                  </Selo>
                ))}
              </div>
            )}

          {/* Ações da linha, alinhadas à direita como na referência. */}
          <div className="mt-1 flex items-center justify-end gap-0.5">
            {mostrar("espera") && espera && (
              <span
                className={cn(
                  "mr-auto shrink-0 whitespace-nowrap text-[11px] font-semibold tabular-nums",
                  espera.classe,
                )}
                title={
                  ticket.status === "pending"
                    ? "Esperando há"
                    : "Sem resposta há"
                }
              >
                {espera.rotulo}
              </span>
            )}

            {loading && (
              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
            )}

            {temBotao("aceitar") && ticket.status === "pending" && (
              <Acao
                disabled={loading}
                onClick={() => mudarStatus("open", user?.id, true)}
              >
                {i18n.t("ticketsList.buttons.accept")}
              </Acao>
            )}

            {temBotao("transferir") &&
              ticket.status !== "closed" &&
              podeMexerNaEspera("tickets:transferPending") && (
              <Acao disabled={loading} onClick={() => setTransferindo(true)}>
                Transferir
              </Acao>
            )}

            {temBotao("resolver") && ticket.status === "open" && (
              <Acao disabled={loading} onClick={() => setResolvendo(true)}>
                Resolver
              </Acao>
            )}

            {temBotao("ignorar") &&
              ticket.status === "pending" &&
              temPermissao("tickets:closePending") && (
              <Acao
                disabled={loading}
                onClick={() => mudarStatus("closed", null)}
              >
                Ignorar
              </Acao>
            )}

            {temBotao("reabrir") && ticket.status === "closed" && (
              <Acao
                disabled={loading}
                onClick={pedirReabertura}
              >
                Reabrir
              </Acao>
            )}

            {/* Estrela: marcação só de quem está logado. Área de toque de
                28px para funcionar no celular sem abrir a conversa por engano. */}
            {temBotao("fixar") && (
              <button
                type="button"
                aria-label={fixado ? "Soltar do topo" : "Fixar no topo"}
                aria-pressed={fixado}
                title={fixado ? "Soltar do topo" : "Fixar no topo"}
                onClick={alternarFixado}
                className={cn(
                  "flex h-6 w-6 items-center justify-center rounded-full transition-colors",
                  fixado
                    ? "text-primary hover:bg-primary/10"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                {fixado ? (
                  <PinOff className="h-3.5 w-3.5" />
                ) : (
                  <Pin className="h-3.5 w-3.5" />
                )}
              </button>
            )}

            {temBotao("naoLida") && ticket.unreadMessages === 0 && (
              <button
                type="button"
                aria-label="Marcar como não lida"
                title="Marcar como não lida"
                onClick={marcarNaoLida}
                disabled={marcandoNaoLida}
                className="flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <MailOpen className="h-3.5 w-3.5" />
              </button>
            )}

            {temBotao("favoritar") && (
            <button
              type="button"
              aria-label={
                favorito ? "Remover dos favoritos" : "Marcar como favorita"
              }
              aria-pressed={favorito}
              title={
                favorito ? "Remover dos favoritos" : "Marcar como favorita"
              }
              onClick={alternarFavorito}
              className={cn(
                "flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors",
                favorito
                  ? "text-amber-500 hover:bg-amber-500/10"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              <Star
                className="h-4 w-4"
                fill={favorito ? "currentColor" : "none"}
              />
            </button>
            )}
          </div>
        </div>

        {/* Horário e contador ficam no canto superior direito, fora do fluxo,
            para não empurrar o nome nem as ações. */}
        <div className="absolute right-3 top-2.5 flex flex-col items-end gap-1">
          <span
            className={cn(
              "text-[11px] tabular-nums",
              naoLidas > 0
                ? "font-bold text-emerald-600 dark:text-emerald-400"
                : "text-muted-foreground",
            )}
          >
            {/* Cabe nos 56px que o `pr-14` reserva: o ano só quando é outro. */}
            {isSameDay(parseISO(dataDoCartao), new Date())
              ? format(parseISO(dataDoCartao), "HH:mm")
              : format(
                  parseISO(dataDoCartao),
                  isSameYear(parseISO(dataDoCartao), new Date())
                    ? "dd/MM"
                    : "dd/MM/yy",
                )}
          </span>

          {naoLidas > 0 && (
            <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-emerald-500 px-1.5 text-[11px] font-bold tabular-nums text-white">
              {naoLidas}
            </span>
          )}
        </div>
      </div>
      )}
    </>
  );
};

export default TicketListItem;
